import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Paper,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import { dataProvider } from "../../providers/dataProvider";

import GpuMetrics, { GpuTelemetry } from "./GpuMetrics";

type Policy = {
  openrouter_workers: number;
  openrouter_concurrency: number;
  runpod_priorities: number[];
  openrouter_priorities: number[];
  min_pods: number;
  max_pods: number;
  autoscaling: boolean;
  target_drain_seconds: number;
  idle_seconds: number;
  max_hourly_usd: number;
  max_daily_usd: number;
};
type Pod = {
  gpu_metrics?: GpuTelemetry | null;
  id: string;
  state: string;
  desired: string;
  manual?: boolean;
  error: string | null;
  provider_id?: string | null;
  heartbeat_at?: string | null;
  created_at?: string;
  active_leases?: number;
  expired_leases?: number;
  remaining_jobs?: number;
  active_requests?: number;
  lease_expires_at?: string | null;
};
const workerStatus = (pod: Pod) => {
  if (pod.desired === "terminating")
    return "Stopping now — interrupting current work";
  if (pod.state === "deleting") return "Deleting pod — verifying shutdown";
  if (pod.desired === "draining") return "Finishing current jobs";
  if (pod.state === "recovering")
    return "Recovering connection — keeping this pod";
  if (pod.state === "reserved") return "Waiting for supervisor to start worker";
  if (pod.state === "starting")
    return pod.provider_id
      ? "Loading model and checking readiness"
      : "Provisioning pod";
  if ((pod.expired_leases || 0) > 0) return "Lease expired — recovery pending";
  if ((pod.active_leases || 0) > 0) return "Processing jobs";
  return "Ready — waiting to claim work";
};
type LegacyWorker = {
  id: string;
  active_leases: number;
  expired_leases: number;
  queued_packets: number;
  revoked: boolean;
};

type Pool = {
  id: string;
  environment: string;
  model: { name: string };
  deployment: string;
  policy: Policy;
  revision: number;
  rate_ceiling: number;
  hard_max_pods: number;
  estimated_hourly_usd: number;
  decision: {
    reason?: string;
    desired_pods?: number;
    estimated_drain_seconds?: number | null;
    measured_jobs_per_gpu_hour?: Record<string, number>;
  };
  startup?: { samples: number; estimated_seconds: number };
  queue?: { waiting_jobs: number };
  openrouter_configured?: boolean;
  remote_failures?: { id: string; error: string; stopped_at: string }[];
  remote_workers?: {
    id: string;
    state: string;
    desired: string;
    concurrency: number;
    active_requests: number;
    remaining_jobs: number;
    heartbeat_at?: string;
    error?: string;
  }[];
  openrouter_usage?: {
    reported_usd: number;
    unpriced_requests: number;
    requests: number;
  };
  pods: Pod[];
  recent_failures?: { id: string; error: string; absent_at: string }[];
  audit: { actor: string; revision: number; created_at: string }[];
};
type View = {
  pools: Pool[];
  manager: { healthy: boolean } | null;
};
type Preview = {
  token: string;
  floor_hourly_usd: number;
  floor_24h_usd: number;
  maximum_hourly_usd: number;
  maximum_daily_usd: number;
  warnings: string[];
};
const dollars = (n: number) => `$${n.toFixed(2)}`;
const operationError = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item) =>
      item && typeof item.msg === "string" ? [item.msg] : []
    );
    if (messages.length) return messages.join("; ");
  }
  return fallback;
};

export default function PoolControls({
  legacyWorkers = [],
}: {
  legacyWorkers?: LegacyWorker[];
}) {
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [editing, setEditing] = useState<Pool | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [manualAction, setManualAction] = useState<{
    pool: Pool;
    pod?: Pod;
    force?: boolean;
    review?: {
      token: string;
      hourly_usd: number;
      reserved_usd: number;
      rental_seconds: number;
    };
  } | null>(null);
  const [rentalMinutes, setRentalMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [changeError, setChangeError] = useState("");
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    let loading = false;
    const load = async () => {
      if (loading || document.hidden) return;
      loading = true;
      clearTimeout(timer);
      try {
        const r = await dataProvider.custom!({
          url: "/llm-work/pools",
          method: "get",
        });
        if (live) {
          setView(r.data as unknown as View);
          setUpdatedAt(new Date());
          setError("");
        }
      } catch {
        if (live)
          setError(
            "GPU pool data is unavailable. Any retained figures may be stale."
          );
      } finally {
        loading = false;
        if (live) timer = setTimeout(load, 2000);
      }
    };
    const visible = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", visible);
    void load();
    return () => {
      live = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refresh]);
  const review = async () => {
    if (!editing || !policy) return;
    setBusy(true);
    setChangeError("");
    try {
      const r = await dataProvider.custom!({
        url: `/llm-work/pools/${encodeURIComponent(editing.id)}/preview`,
        method: "post",
        payload: { policy, revision: editing.revision },
      });
      setPreview(r.data as unknown as Preview);
    } catch (error) {
      setChangeError(
        operationError(
          error,
          "Cannot review this change. Check the limits, or refresh if another admin changed the settings."
        )
      );
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!editing || !preview) return;
    setBusy(true);
    setChangeError("");
    try {
      await dataProvider.custom!({
        url: `/llm-work/pools/${encodeURIComponent(editing.id)}/apply`,
        method: "post",
        payload: { token: preview.token },
      });
      setEditing(null);
      setPreview(null);
      setRefresh((v) => v + 1);
    } catch (error) {
      setChangeError(
        operationError(
          error,
          "Change was not confirmed. Refresh the pool before retrying; the review may have expired."
        )
      );
    } finally {
      setBusy(false);
    }
  };
  const manual = async () => {
    if (!manualAction) return;
    setBusy(true);
    setChangeError("");
    const base = `/llm-work/pools/${encodeURIComponent(
      manualAction.pool.id
    )}/workers`;
    try {
      if (manualAction.pod) {
        await dataProvider.custom!({
          url: `${base}/${encodeURIComponent(manualAction.pod.id)}/${
            manualAction.force ? "kill" : "stop"
          }`,
          method: "post",
        });
      } else if (!manualAction.review) {
        const r = await dataProvider.custom!({
          url: `${base}/preview`,
          method: "post",
          payload: { rental_seconds: rentalMinutes * 60 },
        });
        setManualAction({
          ...manualAction,
          review: r.data as unknown as NonNullable<
            typeof manualAction
          >["review"],
        });
        return;
      } else {
        await dataProvider.custom!({
          url: `${base}/start`,
          method: "post",
          payload: { token: manualAction.review.token },
        });
      }
      setManualAction(null);
      setRefresh((v) => v + 1);
    } catch (error) {
      setChangeError(
        operationError(
          error,
          "Worker change was not confirmed. Check manager health and spending limits; refresh or review again before retrying."
        )
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box mb={3}>
      <Typography variant="h5" mb={1}>
        GPU capacity
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Updates every 2 seconds while visible
        {updatedAt ? ` · Updated ${updatedAt.toLocaleTimeString()}` : ""}
      </Typography>
      {view && view.pools.length > 0 && (
        <Chip
          size="small"
          sx={{ ml: 1 }}
          color={view.manager?.healthy ? "success" : "warning"}
          label={
            view.manager?.healthy
              ? "Supervisor online"
              : "Supervisor heartbeat overdue"
          }
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {view && view.pools.length > 0 && !view.manager?.healthy && (
        <Alert severity="warning">
          The GPU manager is not reporting a recent heartbeat. Settings will
          take effect when it is running.
        </Alert>
      )}
      {view?.pools.length === 0 && (
        <Alert severity="info">
          No managed pools are provisioned. Existing manual workers are
          unaffected.
        </Alert>
      )}
      {view?.pools.map((pool) => (
        <Paper key={pool.id} sx={{ p: 2, mt: 2 }}>
          <Box
            display="flex"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6">{pool.id}</Typography>
              <Typography variant="body2">
                {pool.model.name} · {pool.environment} · {pool.deployment}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                setEditing(pool);
                setPolicy({
                  ...pool.policy,
                  openrouter_workers: pool.policy.openrouter_workers ?? 0,
                  openrouter_concurrency:
                    pool.policy.openrouter_concurrency ?? 16,
                  runpod_priorities: pool.policy.runpod_priorities ?? [1, 2, 3],
                  openrouter_priorities: pool.policy.openrouter_priorities ?? [
                    1, 2, 3,
                  ],
                });
                setPreview(null);
                setChangeError("");
              }}
            >
              Change GPU capacity
            </Button>
          </Box>
          <Box mt={2}>
            <Button
              variant="contained"
              disabled={!view?.manager?.healthy}
              onClick={() => {
                setManualAction({ pool });
                setRentalMinutes(60);
                setChangeError("");
              }}
            >
              Start manual worker
            </Button>
            <Typography variant="body2" mt={1}>
              Manual workers run until stopped or their rental limit is reached.
              They are not automatically replaced.
            </Typography>
            {pool.queue && (
              <Typography mt={1}>
                {pool.queue.waiting_jobs.toLocaleString()} jobs waiting for a
                worker
              </Typography>
            )}
            {pool.pods.length === 0 && !pool.remote_workers?.length && (
              <Typography variant="body2" mt={1}>
                No workers running.
              </Typography>
            )}
            {pool.pods.map((pod) => (
              <Paper
                key={pod.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 1,
                  borderLeft: 4,
                  borderLeftColor: "success.main",
                  bgcolor: "action.hover",
                }}
              >
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                  <Chip size="small" color="success" label="RunPod" />
                  <Typography variant="subtitle2">
                    {pod.manual ? "Manual worker" : "Automatic worker"}
                  </Typography>
                  <Chip label={workerStatus(pod)} />
                  {pod.manual && (
                    <Button
                      color="warning"
                      disabled={
                        pod.desired === "draining" ||
                        pod.desired === "terminating" ||
                        pod.state === "deleting"
                      }
                      onClick={() => {
                        setManualAction({ pool, pod });
                        setChangeError("");
                      }}
                    >
                      Stop after current jobs
                    </Button>
                  )}
                  {pod.manual && (
                    <Button
                      color="error"
                      disabled={
                        pod.desired === "terminating" ||
                        pod.state === "deleting"
                      }
                      onClick={() => {
                        setManualAction({ pool, pod, force: true });
                        setChangeError("");
                      }}
                    >
                      Stop now
                    </Button>
                  )}
                </Box>
                {["reserved", "starting"].includes(pod.state) &&
                  pod.desired !== "draining" && (
                    <LinearProgress
                      aria-label="Worker startup in progress"
                      sx={{ my: 1 }}
                    />
                  )}
                {["reserved", "starting"].includes(pod.state) &&
                  pod.created_at && (
                    <Typography variant="body2" sx={{ my: 1 }}>
                      Started{" "}
                      {Math.max(
                        0,
                        Math.floor(
                          (Date.now() - new Date(pod.created_at).getTime()) /
                            60000
                        )
                      )}
                      m ago ·{" "}
                      {Date.now() - new Date(pod.created_at).getTime() <
                      (pool.startup?.estimated_seconds || 360) * 1000
                        ? `About ${Math.max(
                            1,
                            Math.ceil(
                              ((pool.startup?.estimated_seconds || 360) -
                                (Date.now() -
                                  new Date(pod.created_at).getTime()) /
                                  1000) /
                                60
                            )
                          )}m until ready`
                        : "Taking longer than the startup estimate; still waiting for readiness"}
                      {pool.startup?.samples
                        ? " (based on previous starts)"
                        : " (initial estimate; downloads and warmup can vary)"}
                      . No jobs are claimed until the model is ready.
                    </Typography>
                  )}
                {pod.heartbeat_at &&
                  Date.now() - new Date(pod.heartbeat_at).getTime() > 30000 && (
                    <Alert severity="warning">
                      Worker heartbeat is delayed. The supervisor may be
                      recovering this worker.
                    </Alert>
                  )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {pod.id}
                  {pod.provider_id ? ` · Pod ${pod.provider_id}` : ""}
                </Typography>
                <Typography variant="body2">
                  {pod.active_leases || 0} active packet leases ·{" "}
                  {pod.remaining_jobs || 0} jobs remaining ·{" "}
                  {pod.active_requests || 0} requests in flight
                </Typography>
                {pod.lease_expires_at && (
                  <Typography variant="caption">
                    Lease expires{" "}
                    {new Date(pod.lease_expires_at).toLocaleTimeString()}.{" "}
                  </Typography>
                )}
                {pod.heartbeat_at && (
                  <Typography variant="caption">
                    Worker heartbeat{" "}
                    {new Date(pod.heartbeat_at).toLocaleTimeString()}
                  </Typography>
                )}
                <GpuMetrics
                  metrics={pod.gpu_metrics}
                  pending={
                    pod.state === "ready"
                      ? (pod.remaining_jobs || 0) +
                        (pool.queue?.waiting_jobs || 0)
                      : 0
                  }
                  active={pod.active_requests || 0}
                />
                {pod.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {pod.error}
                  </Alert>
                )}
              </Paper>
            ))}
          </Box>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
            gap={1}
            mt={1}
          >
            {pool.remote_workers?.map((worker) => (
              <Paper
                key={worker.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderLeft: 4,
                  borderLeftColor: "secondary.main",
                  bgcolor: "action.hover",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip size="small" color="secondary" label="OpenRouter" />
                  <Typography variant="subtitle2">
                    {worker.desired === "draining"
                      ? "Finishing current jobs"
                      : worker.state}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {worker.active_requests}/{worker.concurrency} requests in
                  flight · {worker.remaining_jobs} jobs remaining
                </Typography>
                <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
                  {worker.id}
                </Typography>
                {(!worker.heartbeat_at ||
                  Date.now() - new Date(worker.heartbeat_at).getTime() >
                    30000) && (
                  <Alert severity="warning">
                    Waiting for a recent worker heartbeat.
                  </Alert>
                )}
                {worker.error && <Alert severity="error">{worker.error}</Alert>}
              </Paper>
            ))}
          </Box>
          {pool.remote_failures?.map((failure) => (
            <Alert key={failure.id} severity="warning" sx={{ mt: 1 }}>
              OpenRouter worker stopped: {failure.error}. Check the supervisor
              key/model route. Replacements pause for five minutes after
              failure.
            </Alert>
          ))}
          {pool.openrouter_usage && (
            <Typography variant="body2" mt={1} color="secondary.main">
              OpenRouter last 24h:{" "}
              {dollars(Number(pool.openrouter_usage.reported_usd))} reported ·{" "}
              {pool.openrouter_usage.requests} requests ·{" "}
              {pool.openrouter_usage.unpriced_requests} not yet priced. No
              spending cap.
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={1} my={1}>
            <Chip
              size="small"
              color="secondary"
              label={`OpenRouter floor ${
                pool.policy.openrouter_workers || 0
              } · P${(pool.policy.openrouter_priorities || [1, 2, 3]).join(
                ", P"
              )}`}
            />
            <Chip
              size="small"
              color="success"
              label={`RunPod P${(
                pool.policy.runpod_priorities || [1, 2, 3]
              ).join(", P")}`}
            />
            <Chip label={`Minimum ${pool.policy.min_pods} GPUs`} />
            <Chip
              label={`Autoscaling ${pool.policy.autoscaling ? "on" : "off"}`}
            />
            <Chip label={`Maximum ${pool.policy.max_pods} GPUs`} />
            <Chip
              label={`Current estimated burn ${dollars(
                pool.estimated_hourly_usd
              )}/hour`}
            />
            {[
              "reserved",
              "starting",
              "ready",
              "recovering",
              "draining",
              "deleting",
            ]
              .filter((state) =>
                pool.pods.some(
                  (p) =>
                    p.state === state ||
                    (state === "draining" && p.desired === "draining")
                )
              )
              .map((state) => (
                <Chip
                  key={state}
                  label={`${state}: ${
                    pool.pods.filter((p) =>
                      state === "draining"
                        ? p.desired === "draining" && p.state !== "deleting"
                        : p.state === state &&
                          !(state === "ready" && p.desired === "draining")
                    ).length
                  }`}
                />
              ))}
          </Box>
          {pool.policy.min_pods > 0 && (
            <Alert severity="warning">
              The minimum keeps GPUs billing when idle, even with autoscaling
              off. Estimated floor:{" "}
              {dollars(pool.policy.min_pods * pool.rate_ceiling)}/hour.
            </Alert>
          )}
          {pool.pods.some((p) => p.error) && (
            <Alert severity="error">
              A GPU needs attention:{" "}
              {pool.pods
                .filter((p) => p.error)
                .map((p) => `${p.id}: ${p.error}`)
                .join("; ")}
              . Unconfirmed shutdowns still count towards spending limits.
            </Alert>
          )}
          {pool.recent_failures?.map((failure) => (
            <Alert key={failure.id} severity="warning" sx={{ mt: 1 }}>
              Worker stopped at{" "}
              {new Date(failure.absent_at).toLocaleTimeString()}:{" "}
              {failure.error}. Pod deletion was verified; unfinished work
              remains recoverable.
            </Alert>
          ))}
          <Typography mt={1}>
            {pool.decision.reason ||
              "Waiting for the manager to evaluate this pool."}
          </Typography>
          <Typography variant="body2" mt={1}>
            {pool.decision.estimated_drain_seconds != null
              ? `Estimated queue completion: about ${Math.ceil(
                  pool.decision.estimated_drain_seconds / 60
                )} minutes, assuming no new work.`
              : "Queue completion estimate is not available yet; measuring successful job throughput."}
          </Typography>
          <Typography variant="body2">
            Measured jobs per busy GPU-hour:{" "}
            {Object.entries(pool.decision.measured_jobs_per_gpu_hour || {})
              .map(([w, n]) => `${w}: ${n.toFixed(1)}`)
              .join(" · ") ||
              "Not enough samples yet; automatic expansion is limited to one probe GPU."}
          </Typography>
          {pool.audit[0] && (
            <Typography variant="caption">
              Last changed by {pool.audit[0].actor} at{" "}
              {new Date(pool.audit[0].created_at).toLocaleString()} (revision{" "}
              {pool.revision})
            </Typography>
          )}
        </Paper>
      ))}
      {legacyWorkers.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Other workers</Typography>
          {legacyWorkers.map((worker) => (
            <Typography key={worker.id} variant="body2">
              {worker.id}:{" "}
              {worker.revoked
                ? "Revoked"
                : worker.active_leases
                ? "Processing jobs"
                : "Waiting for worker"}{" "}
              · {worker.active_leases} active leases · {worker.queued_packets}{" "}
              queued packets
            </Typography>
          ))}
        </Paper>
      )}
      <Dialog
        open={!!manualAction}
        onClose={() => {
          if (!busy) setManualAction(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {manualAction?.force
            ? "Stop worker immediately"
            : manualAction?.pod
            ? "Stop manual worker"
            : "Start manual worker"}{" "}
          — {manualAction?.pool.id}
        </DialogTitle>
        <DialogContent dividers>
          {changeError && <Alert severity="error">{changeError}</Alert>}
          {manualAction?.force ? (
            <Alert severity="error">
              Interrupt current jobs and delete this pod now. Accepted stages
              are retained; unfinished work returns to the queue after lease
              recovery. Billing continues until deletion is verified.
            </Alert>
          ) : manualAction?.pod ? (
            <Alert severity="warning">
              This worker will stop pulling packets, finish every job in its
              claimed packets, then shut down its pod. Billing continues until
              deletion is verified. Queued work stays available for other
              workers. Rental and failure limits still apply.
            </Alert>
          ) : (
            <Box>
              {!manualAction?.review && (
                <TextField
                  fullWidth
                  label="Maximum runtime (minutes)"
                  type="number"
                  value={rentalMinutes}
                  onChange={(e) => setRentalMinutes(Number(e.target.value))}
                  inputProps={{ min: 10, max: 120, step: 5 }}
                  helperText="10–120 minutes, including startup. You can stop the worker earlier."
                  sx={{ mb: 2 }}
                />
              )}
              <Alert severity="warning">
                Starting a worker incurs GPU charges, including startup and idle
                time. It runs independently of autoscaling and shares this
                pool’s spending limits.
                {manualAction?.review && (
                  <Typography mt={1}>
                    Up to {dollars(manualAction.review.hourly_usd)}/hour.
                    Reserved rental: {dollars(manualAction.review.reserved_usd)}{" "}
                    for {manualAction.review.rental_seconds / 60} minutes. The
                    hard rental limit can interrupt unfinished work; accepted
                    stages are retained for recovery.
                  </Typography>
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setManualAction(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              busy ||
              (!manualAction?.pod &&
                !manualAction?.review &&
                (!Number.isInteger(rentalMinutes) ||
                  rentalMinutes < 10 ||
                  rentalMinutes > 120))
            }
            color={manualAction?.force ? "error" : "warning"}
            variant="contained"
            onClick={() => void manual()}
          >
            {busy
              ? "Working…"
              : manualAction?.force
              ? "Confirm stop now"
              : manualAction?.pod
              ? "Confirm stop after current jobs"
              : manualAction?.review
              ? `Start — up to ${dollars(manualAction.review.hourly_usd)}/hour`
              : "Review worker spending"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!editing}
        onClose={() => {
          if (!busy) setEditing(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {preview ? "Review GPU spending change" : "GPU capacity settings"} —{" "}
          {editing?.id}
        </DialogTitle>
        <DialogContent dividers>
          {changeError && <Alert severity="error">{changeError}</Alert>}
          {preview ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Alert severity="warning">
                <strong>This change can incur GPU charges.</strong>
                <br />
                Minimum: {dollars(preview.floor_hourly_usd)}/hour (
                {dollars(preview.floor_24h_usd)} over 24 hours before caps).
                <br />
                Maximum: {dollars(preview.maximum_hourly_usd)}/hour; rolling
                24-hour cap {dollars(preview.maximum_daily_usd)}.
              </Alert>
              <Typography>
                Minimum GPUs: {editing?.policy.min_pods} → {policy?.min_pods}.
                Autoscaling: {editing?.policy.autoscaling ? "on" : "off"} →{" "}
                {policy?.autoscaling ? "on" : "off"}. Maximum GPUs:{" "}
                {editing?.policy.max_pods} → {policy?.max_pods}.
              </Typography>
              <Typography>
                OpenRouter workers: {editing?.policy.openrouter_workers || 0} →{" "}
                {policy?.openrouter_workers}. Concurrency per worker:{" "}
                {policy?.openrouter_concurrency}.
              </Typography>
              <Typography>
                RunPod priorities: {policy?.runpod_priorities.join(", ")}.
                OpenRouter priorities:{" "}
                {policy?.openrouter_priorities.join(", ")}. Priority 1 goes
                first; active packets finish.
              </Typography>
              {preview.warnings.map((w) => (
                <Typography key={w} variant="body2">
                  {w}
                </Typography>
              ))}
            </Box>
          ) : (
            policy && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Alert severity="warning">
                  A positive minimum costs money while idle. Turning autoscaling
                  off maintains the minimum.
                </Alert>
                <Typography variant="subtitle2" color="secondary.main">
                  OpenRouter overflow
                </Typography>
                {!editing?.openrouter_configured && (
                  <Alert severity="info">
                    Configure the model route and supervisor key before enabling
                    OpenRouter workers.
                  </Alert>
                )}
                <TextField
                  label="OpenRouter worker floor"
                  type="number"
                  value={policy.openrouter_workers}
                  disabled={!editing?.openrouter_configured}
                  inputProps={{ min: 0, max: 20 }}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      openrouter_workers: Number(e.target.value),
                    })
                  }
                />
                <TextField
                  label="OpenRouter concurrency per worker"
                  type="number"
                  value={policy.openrouter_concurrency}
                  inputProps={{ min: 1, max: 64 }}
                  onChange={(e) =>
                    setPolicy({
                      ...policy,
                      openrouter_concurrency: Number(e.target.value),
                    })
                  }
                />
                <Alert severity="warning">
                  OpenRouter has no spend cap. Standby is free; requests incur
                  provider charges when GPUs are occupied or warming up.
                </Alert>
                {(["runpod_priorities", "openrouter_priorities"] as const).map(
                  (key) => (
                    <Box key={key}>
                      <Typography variant="subtitle2">
                        {key === "runpod_priorities" ? "RunPod" : "OpenRouter"}{" "}
                        priorities
                      </Typography>
                      {[1, 2, 3].map((priority) => (
                        <FormControlLabel
                          key={priority}
                          label={`P${priority} ${
                            priority === 1
                              ? "Live"
                              : priority === 2
                              ? "Shadow"
                              : "Background"
                          }`}
                          control={
                            <Checkbox
                              size="small"
                              checked={policy[key].includes(priority)}
                              onChange={(_, checked) =>
                                setPolicy({
                                  ...policy,
                                  [key]: checked
                                    ? [...policy[key], priority].sort()
                                    : policy[key].filter((p) => p !== priority),
                                })
                              }
                            />
                          }
                        />
                      ))}
                    </Box>
                  )
                )}
                <FormControlLabel
                  label="Autoscaling"
                  control={
                    <Switch
                      checked={policy.autoscaling}
                      onChange={(_, v) =>
                        setPolicy({ ...policy, autoscaling: v })
                      }
                    />
                  }
                />
                {(
                  [
                    ["min_pods", "Minimum GPUs"],
                    ["max_pods", "Maximum GPUs"],
                    ["target_drain_seconds", "Target drain time (seconds)"],
                    ["idle_seconds", "Idle grace (seconds)"],
                    ["max_hourly_usd", "Hourly spending cap (USD)"],
                    ["max_daily_usd", "Rolling 24-hour spending cap (USD)"],
                  ] as const
                ).map(([key, label]) => (
                  <TextField
                    key={key}
                    label={label}
                    type="number"
                    helperText={
                      key === "max_pods"
                        ? `Server limit: ${editing?.hard_max_pods} GPUs`
                        : undefined
                    }
                    value={policy[key]}
                    onChange={(e) =>
                      setPolicy({ ...policy, [key]: Number(e.target.value) })
                    }
                    inputProps={{
                      min: key === "max_pods" ? 1 : 0,
                      max:
                        key === "max_pods" ? editing?.hard_max_pods : undefined,
                      step: key.includes("usd") ? 0.01 : 1,
                    }}
                  />
                ))}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => (preview ? setPreview(null) : setEditing(null))}
          >
            {preview ? "Back" : "Cancel"}
          </Button>
          <Button
            color={preview ? "warning" : "primary"}
            variant="contained"
            disabled={
              busy ||
              !policy?.runpod_priorities.length ||
              !policy?.openrouter_priorities.length
            }
            onClick={() => void (preview ? apply() : review())}
          >
            {busy
              ? "Working…"
              : preview
              ? policy && policy.openrouter_workers > 0
                ? "Apply — OpenRouter uncapped"
                : `Apply — up to ${dollars(preview.maximum_hourly_usd)}/hour`
              : "Review spending impact"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
